import type { ProductAccommodationFacilities } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchPageData } from '@/app/search/createSearchPageData';
import type { StarfishIconRef } from '@/app/common/icons/starfish';

// Payment card configuration
// Eurocard and Euro Mastercard will be the same logo as Mastercard so we will be filtering them out in the UI
const SUPPORTED_CARD_CODES: readonly SupportedCardCode[] = ['AX', 'DC', 'DS', 'JC', 'MC', 'MS', 'VI'];

const creditCardIconNames: Record<SupportedCardCode, string> = {
  AX: 'american-express',
  DC: 'diners-club',
  DS: 'discover',
  JC: 'jcb',
  MC: 'mastercard',
  MS: 'maestro',
  VI: 'visa',
};

export type HotelStaticAmenity = {
  label: string;
  icon?: StarfishIconRef;
};

export type HotelStaticData = {
  accommodationType: AccommodationType;
  amenities: HotelStaticAmenity[];
  // Optional because they genuinely can be: an error-shaped response carries no accommodation, so
  // the adapter yields nothing for either. Marking them required let call sites dereference them
  // bare and crash on exactly those responses (MPH-1007).
  checkinInformation?: CheckInformation;
  checkoutInformation?: CheckInformation;
  city: string;
  cityCenterCoordinates?: LatLng;
  cityId: string;
  coordinates?: LatLng;
  countryId: string;
  countryName: string;
  description?: [string, string[]][];
  direction: string;
  distanceToCityCenter?: number;
  dsaInfo?: DsaInfo;
  hotelOpinion: HotelOpinion;
  images: string[];
  isTopSecret?: boolean;
  name: string;
  rating: number;
  registrationNumber?: string;
  services?: HotelServiceGroup[];
  withoutAirport: boolean;
  badges?: string[];
  fees?: {
    optional: string[] | null;
    mandatory: string[] | null;
  };
  houseRules?: string[];
  spokenLanguages?: string[];
  paymentTypes?: PaymentTypeCode[];
  policies?: HotelPolicies;
  sustainabilityCertifications?: SustainabilityCertification[];
};

export function adaptHotelStaticDataResponse(
  staticData: HotelDetailSuccess,
  t: TranslationFunction,
  fallbackImages?: string[]
): HotelStaticData {
  const {
    accommodationType,
    address,
    amenities,
    category,
    checkinInformation,
    checkoutInformation,
    city,
    countryId,
    countryName,
    coordinates,
    cityCenterCoordinates,
    descriptionMap,
    distanceToCityCenter,
    dsaInfo,
    images,
    internalCityId,
    name,
    serviceMap,
    topSecret,
    withoutAirport,
    registrationNumber,
    badges,
    fees,
    houseRules,
    spokenLanguages,
    paymentTypes,
    policies,
    sustainabilityCertifications,
  } = staticData;

  return {
    accommodationType,
    amenities: adaptHotelAmenities(amenities, serviceMap, t) ?? [],
    services:
      serviceMap != null
        ? Object.entries(serviceMap).map(([serviceCategory, services]) => ({
            category: serviceCategory as HotelServicesCategories,
            services:
              (services as HotelService[])
                // A blank label counts as no label: travel-api sends whitespace-only ones, and
                // they otherwise reach every consumer as a nameless entry.
                .filter((service: HotelService) => service.label?.trim())
                .map((service: HotelService) =>
                  service.hasCost
                    ? `${service.label.trim()} ${t('vacation.common.hotelservicetype.extracharge')}`
                    : service.label.trim()
                ) || [],
          }))
        : undefined,
    checkinInformation,
    checkoutInformation,
    city,
    cityCenterCoordinates,
    cityId: internalCityId,
    coordinates,
    countryId,
    countryName,
    description: descriptionMap != null ? getSortedDescription(Object.entries(descriptionMap)) : undefined,
    direction: adaptAddress(address),
    distanceToCityCenter,
    dsaInfo,
    hotelOpinion: {
      rating: staticData.hotelOpinion?.rating,
      numberOfReviews: staticData.hotelOpinion?.reviews,
      opinionId: staticData.hotelOpinion?.opinionId,
      categories: getTranslatedCategories(staticData.hotelOpinion?.categories, t),
      // Detail trusts ONLY its own feed (shopping-api / travel-api) for the review provider. No
      // memsearch backfill: if travel-api omits `opinion.source.name` the source stays absent and
      // the reviews are hidden, so search and detail each resolve reviews from a single source and
      // can't disagree mid-page.
      reviewSource: staticData.hotelOpinion?.reviewSource,
    },
    images: syncImagesWithFallback(images || [], fallbackImages),
    isTopSecret: topSecret,
    name,
    rating: category?.quantity ?? 0,
    withoutAirport,
    registrationNumber,
    badges,
    fees,
    houseRules,
    spokenLanguages,
    paymentTypes: paymentTypes ? getUniquePaymentTypes(paymentTypes) : undefined,
    policies,
    sustainabilityCertifications,
  };
}

function getImageKey(url: string): string {
  // A versioned delivery path identifies the asset on its own: /v1701333934/<public id>
  const versionedPathMatch = url.match(/\/v\d+\/.+$/);
  if (versionedPathMatch) return versionedPathMatch[0];
  // Normalize protocol for the rest: "https://..." and "//..." become "//..."
  return url.replace(/^https?:/, '');
}

function syncImagesWithFallback(images: string[], fallbackImages: string[] | undefined): string[] {
  if (!fallbackImages || fallbackImages.length === 0) return images;
  const imagesByKey = new Map(images.map((img) => [getImageKey(img), img]));
  const fallbackKeys = new Set(fallbackImages.map(getImageKey));
  const orderedFromFallback = fallbackImages
    .map((img) => imagesByKey.get(getImageKey(img)))
    .filter((img): img is string => img !== undefined);
  const rest = images.filter((img) => !fallbackKeys.has(getImageKey(img)));
  return [...orderedFromFallback, ...rest];
}

export function getTranslatedCategories(categories: Category[] | undefined, t: TranslationFunction): Category[] {
  return categories
    ? categories.map((category) => {
        const key = MAP_KEYS_RATING_CATEGORIES[category.name];
        // t() returns '' for keys missing from the runtime dictionary — fall back to the raw name
        // rather than rendering an empty label (e.g. 'wifi' is absent from some CMS dictionaries).
        const translated = key ? t(key) : '';
        return { ...category, name: translated || category.name };
      })
    : [];
}

const descriptionOrder = [
  'GENERAL',
  'LOCATION',
  'EXTERIOR',
  'RESTAURANT',
  'FACILITIES',
  'SPORT',
  'ROOMS',
  'CANCELLATION_POLICY',
  'PAYMENT_INFO',
  'OTHER_INFOS',
];

// travel-api sends a description map that can be empty, or full of entries whose texts are all
// blank. Both used to reach the overview section as a truthy (but unrenderable) array, so hotels
// with no editorial content got an "About this property" heading over nothing. Dropping the empty
// texts here lets every consumer keep its plain `description &&` guard.
function getSortedDescription(description: [string, string[]][] | null | undefined) {
  const withContent = description
    ?.map(([title, texts]): [string, string[]] => [title, texts?.filter((text) => text?.trim()) ?? []])
    .filter(([, texts]) => texts.length > 0)
    .sort(([titleA], [titleB]) => descriptionOrder.indexOf(titleA) - descriptionOrder.indexOf(titleB));

  return withContent?.length ? withContent : undefined;
}

// A safety net, not the fix: the address is composed from street/city/country in
// travel-api/fetchHotelStatic, which now joins only the parts that exist. This still drops a
// stringified segment so an address composed anywhere else — the memsearch fallback, an older
// cached payload — cannot print "null, Kamari, Greece" at the user.
function adaptAddress(address: string | undefined) {
  return (
    address
      ?.split(',')
      .map((segment) => segment.trim())
      .filter((segment) => segment && segment !== 'null' && segment !== 'undefined')
      .join(', ') ?? ''
  );
}

const MAP_KEYS_RATING_CATEGORIES: { [key: string]: string } = {
  AMENITIES: 'vacation.select.details.categories.amenities',
  BAR: 'vacation.select.details.categories.bar',
  BEACH: 'vacation.select.details.categories.beach',
  BREAKFAST: 'vacation.select.details.categories.breakfast',
  CLEANLINESS: 'vacation.select.details.categories.cleanliness',
  COMFORT: 'vacation.select.details.categories.comfort',
  CONDITION: 'vacation.select.details.categories.condition',
  FOOD: 'vacation.select.details.categories.food',
  LOCATION: 'vacation.select.details.categories.location',
  NEIGHBOURHOOD: 'vacation.select.details.categories.neighbourhood',
  POOL: 'vacation.select.details.categories.pool',
  ROOM: 'vacation.select.details.categories.room',
  SERVICE: 'vacation.select.details.categories.service',
  SUSTAINABILITY: 'vacation.select.details.categories.sustainability',
  VALUE: 'vacation.select.details.categories.value',
  VIBE: 'vacation.select.details.categories.vibe',
  WELLNESS: 'vacation.select.details.categories.wellness',
  WELLNESS_AREA: 'vacation.select.details.categories.wellness_area',
  WIFI: 'vacation.select.details.categories.wifi',
};

function adaptHotelAmenities(
  amenities: HotelAmenity[] | undefined,
  serviceMap: HotelServiceMap | undefined,
  t: TranslationFunction
): HotelStaticAmenity[] | undefined {
  // Adds Services Categories to amenities array to have more amenities to show
  const amenitiesFromServices = serviceMap
    ? Object.entries(serviceMap)
        .map(([serviceGroupKey, services]) => {
          const amenityId = MAP_SERVICES_AMENITIES[serviceGroupKey as HotelServicesCategories] || serviceGroupKey;
          return {
            id: amenityId,
            // Pick the first facility that actually carries a label: travel-api sorts empty
            // labels first, and a category can arrive with no labelled facility at all.
            // getAmenityLabel then falls back to the translation of the amenity it maps to.
            label: services.find((service) => service.label?.trim())?.label.trim() ?? '',
          } as HotelAmenity;
        })
        .filter((amenity): amenity is HotelAmenity => !!amenity?.id && !amenities?.find((a) => a.id === amenity.id))
    : [];
  // HEALTH_SAFETY is not available in the facilities filters in the search page
  return amenitiesFromServices
    .concat(amenities || [])
    .filter((amenity) => !['HEALTH_SAFETY', 'GENERAL'].includes(amenity.id))
    .sort(sortAmenities)
    .map((amenity) => ({
      icon: getAmenityIcon(amenity.id),
      label: getAmenityLabel(amenity.id, amenity.label, t),
    }));
}

export function adaptSearchPageHotelData(
  searchPageData: SearchPageData,
  internalHotelId: number,
  t: TranslationFunction
): HotelStaticData | null | undefined {
  const hotelCardData = searchPageData?.productsData?.dealCards?.find(
    (card) => card.locales?.hotelId === internalHotelId
  );
  if (!hotelCardData) return undefined;

  const hotelStaticData = hotelCardData.product;

  return {
    // TODO
    accommodationType: 'HOTELS',
    services: undefined,
    amenities:
      adaptHotelAmenities(
        productFacilitiesToHotelAmenities(hotelCardData.product.accommodation.facilities),
        undefined,
        t
      ) ?? [],
    checkinInformation: {
      from: '',
      to: '',
    },
    checkoutInformation: {
      from: '',
      to: '',
    },
    coordinates: undefined,
    city: hotelStaticData.geo.cityName,
    cityId: hotelStaticData.geo.internalIdCity.toString(),
    countryId: hotelStaticData.geo.countryCode,
    countryName: hotelStaticData.geo.countryName,
    cityCenterCoordinates: undefined,
    description: undefined,
    direction: adaptAddress(hotelStaticData.accommodation.address),
    distanceToCityCenter: hotelStaticData.geo.distanceKm * 1000,
    hotelOpinion: {
      rating: hotelStaticData.accommodation.rating,
      numberOfReviews: hotelStaticData.accommodation.reviewsNum,
      opinionId: '',
      categories: [],
      // No review provider is seeded from memsearch: the review widget stays hidden during this
      // placeholder and only paints once the authoritative shopping-api (travel-api) data resolves,
      // so detail never shows a memsearch-derived score that then flips. `reviewSource` is left absent.
    },
    images: hotelStaticData.accommodation.gallery,
    isTopSecret: hotelCardData.promotionTheme === 'TOP_SECRET_HOTELS',
    name: hotelStaticData.accommodation.name,
    rating: hotelStaticData.accommodation.stars,
    withoutAirport: false,
    // Adding missing required properties
    dsaInfo: undefined,
    registrationNumber: undefined,
    badges: undefined,
    fees: undefined,
    houseRules: undefined,
    spokenLanguages: undefined,
    paymentTypes: undefined,
    policies: undefined,
    sustainabilityCertifications: undefined,
  };
}

function sortAmenities(a: HotelAmenity, b: HotelAmenity): number {
  return SORTED_AMENITIES.indexOf(a.id) - SORTED_AMENITIES.indexOf(b.id);
}

function productFacilitiesToHotelAmenities(productFacilities: ProductAccommodationFacilities): HotelAmenities {
  const amenities: HotelAmenities = [];

  Object.entries(productAccommodationFacilitiesMapping).forEach(([key, value]) => {
    if (productFacilities[key as keyof ProductAccommodationFacilities]) {
      amenities.push({ id: value, label: '' });
    }
  });

  return amenities;
}

function getAmenityIcon(id: HotelAmenityId): StarfishIconRef | undefined {
  return AMENITY_ICONS_MAP[id];
}

function getAmenityLabel(id: HotelAmenityId, label: string, t: TranslationFunction): string {
  const key = AMENITY_TRANSLATION_KEYS_MAP[id];
  return label || (key ? t(key) : '');
}

export function getPolicyTranslationKey(category: string): string {
  const key = POLICY_TRANSLATION_KEYS_MAP[category as HotelPolicySection];
  return key || POLICY_TRANSLATION_KEYS_MAP.GENERAL;
}

export function getPolicyIcon(category: string): StarfishIconRef {
  return POLICY_ICONS_MAP[category as HotelPolicySection] || POLICY_ICONS_MAP.GENERAL;
}

/**
 * Consolidates different Mastercard payment types (CA, MC, UE) into a single 'MC' entry
 * while preserving the order and keeping other payment types unchanged.
 * This prevents showing multiple Mastercard logos in the UI.
 */
function getUniquePaymentTypes(paymentTypes: PaymentTypeCode[]): PaymentTypeCode[] {
  const MASTERCARD_TYPES = new Set(['CA', 'MC', 'UE']);

  let mastercardAdded = false;

  return paymentTypes.reduce<PaymentTypeCode[]>((result, paymentType) => {
    if (MASTERCARD_TYPES.has(paymentType)) {
      if (!mastercardAdded) {
        result.push('MC');
        mastercardAdded = true;
      }
    } else {
      result.push(paymentType);
    }
    return result;
  }, []);
}

/**
 * Checks if a payment type is a supported card type with an icon
 * @param paymentType - The payment type to check
 * @returns True if the payment type is a supported card with an available icon
 */
export function isSupportedCardType(paymentType: PaymentTypeCode): paymentType is SupportedCardCode {
  return (SUPPORTED_CARD_CODES as readonly string[]).includes(paymentType);
}

/**
 * Gets the Content Hub asset name for a supported card type
 * @param cardType - The supported card type
 * @returns The identifier used in the card icon's url
 */
export function getCardIconName(cardType: SupportedCardCode): string {
  return creditCardIconNames[cardType];
}

// Extra amenities from services
type ExtraAmenityId =
  | 'LANGUAGES_SPOKEN'
  | 'FOOD_AND_DRINK'
  | 'ACTIVITIES'
  | 'MEDIA_AND_TECHNOLOGY'
  | 'BUSINESS_EQUIPMENT'
  | 'BATHROOM'
  | 'CLEANING_SERVICES'
  | 'RECEPTION_SERVICES';

const AMENITY_ICONS_MAP: { [key in HotelAmenityId | ExtraAmenityId]: StarfishIconRef } = {
  ADULTS_ONLY: { category: 'Services', name: 'icon-adults-only', color: 'iconPrimary' },
  ENTERTAINMENT: { category: 'Services', name: 'icon-entertainment', color: 'iconPrimary' },
  FACILITIES_CHILDREN: { category: 'Services', name: 'icon-child', color: 'iconPrimary' },
  FACILITIES_DISABLE_GUEST: { category: 'People and users', name: 'icon-accessible-service', color: 'iconPrimary' },
  FREE_WIFI: { category: 'Services', name: 'icon-wifi', color: 'iconPrimary' },
  HEALTH_SAFETY: { category: 'Services', name: 'icon-sanitizer', color: 'iconPrimary' },
  KITCHEN: { category: 'Services', name: 'icon-oven', color: 'iconPrimary' },
  PARKING: { category: 'Services', name: 'icon-parking', color: 'iconPrimary' },
  PETS_ALLOWED: { category: 'Services', name: 'icon-pawn', color: 'iconPrimary' },
  POOL: { category: 'Services', name: 'icon-swimming-pool', color: 'iconPrimary' },
  SEAFRONT: { category: 'Services', name: 'icon-island', color: 'iconPrimary' },
  SPA: { category: 'Services', name: 'icon-wellness', color: 'iconPrimary' },
  // FROM SERVICES
  LANGUAGES_SPOKEN: { category: 'UI Essentials', name: 'icon-language', color: 'iconPrimary' },
  FOOD_AND_DRINK: { category: 'Food and beverages', name: 'icon-cocktail', color: 'iconPrimary' },
  ACTIVITIES: { category: 'Services', name: 'icon-tennis', color: 'iconPrimary' },
  MEDIA_AND_TECHNOLOGY: { category: 'Services', name: 'icon-media', color: 'iconPrimary' },
  BUSINESS_EQUIPMENT: { category: 'Services', name: 'icon-briefcase', color: 'iconPrimary' },
  BATHROOM: { category: 'Services', name: 'icon-bathtub', color: 'iconPrimary' },
  CLEANING_SERVICES: { category: 'Services', name: 'icon-broom', color: 'iconPrimary' },
  RECEPTION_SERVICES: { category: 'Services', name: 'icon-reception', color: 'iconPrimary' },
};

const productAccommodationFacilitiesMapping: { [key in keyof ProductAccommodationFacilities]: HotelAmenityId } = {
  adultsOnly: 'ADULTS_ONLY',
  children: 'FACILITIES_CHILDREN',
  disableGuest: 'FACILITIES_DISABLE_GUEST',
  entertainment: 'ENTERTAINMENT',
  freeWifi: 'FREE_WIFI',
  healthSafety: 'HEALTH_SAFETY',
  kitchen: 'KITCHEN',
  parking: 'PARKING',
  petsAllowed: 'PETS_ALLOWED',
  pool: 'POOL',
  seafront: 'SEAFRONT',
  spa: 'SPA',
};

const SORTED_AMENITIES: (HotelAmenityId | ExtraAmenityId)[] = [
  'POOL',
  'SEAFRONT',
  'FREE_WIFI',
  'FACILITIES_CHILDREN',
  'SPA',
  'PARKING',
  'KITCHEN',
  'PETS_ALLOWED',
  'ADULTS_ONLY',
  'FACILITIES_DISABLE_GUEST',
  'ENTERTAINMENT',
  'HEALTH_SAFETY',
  // FROM SERVICES
  'LANGUAGES_SPOKEN',
  'FOOD_AND_DRINK',
  'ACTIVITIES',
  'MEDIA_AND_TECHNOLOGY',
  'BUSINESS_EQUIPMENT',
  'BATHROOM',
  'CLEANING_SERVICES',
  'RECEPTION_SERVICES',
];

const MAP_SERVICES_AMENITIES: Partial<{ [key in HotelServicesCategories]: HotelAmenityId }> = {
  SWIMMING_POOL: 'POOL',
  INTERNET: 'FREE_WIFI',
  CHILDREN: 'FACILITIES_CHILDREN',
  PARKING: 'PARKING',
  HEALTH_SAFETY: 'HEALTH_SAFETY',
  PETS: 'PETS_ALLOWED',
  ADULTS_ONLY: 'ADULTS_ONLY',
  ACCESSIBILITY: 'FACILITIES_DISABLE_GUEST',
};

const AMENITY_TRANSLATION_KEYS_MAP: { [key in HotelAmenityId | ExtraAmenityId]: string } = {
  ADULTS_ONLY: 'vacation.search.filters.facilities.options.adultsonly',
  ENTERTAINMENT: 'vacation.search.filters.facilities.options.entertainment',
  FACILITIES_CHILDREN: 'vacation.search.filters.facilities.options.children',
  FACILITIES_DISABLE_GUEST: 'vacation.search.filters.facilities.options.disableguest',
  FREE_WIFI: 'vacation.search.filters.facilities.options.freewifi',
  HEALTH_SAFETY: 'vacation.select.health.safety',
  KITCHEN: 'vacation.search.filters.facilities.options.kitchen',
  PARKING: 'vacation.search.filters.facilities.options.parking',
  PETS_ALLOWED: 'vacation.search.filters.facilities.options.pets',
  POOL: 'vacation.search.filters.facilities.options.swimmingpool',
  SEAFRONT: 'vacation.search.filters.facilities.options.seafront',
  SPA: 'vacation.search.filters.facilities.options.spa',
  // FROM SERVICES
  LANGUAGES_SPOKEN: 'vacation.common.hotelservicetype.languagespoken',
  ACTIVITIES: 'vacation.common.hotelservicetype.activities',
  FOOD_AND_DRINK: 'vacation.common.hotelservicetype.fooddrink',
  MEDIA_AND_TECHNOLOGY: 'vacation.common.hotelservicetype.technology',
  BUSINESS_EQUIPMENT: 'vacation.common.hotelservicetype.businessequipment',
  BATHROOM: 'vacation.common.hotelservicetype.bathroom',
  CLEANING_SERVICES: 'vacation.common.hotelservicetype.cleaningservices',
  RECEPTION_SERVICES: 'vacation.common.hotelservicetype.receptionservices',
};

const POLICY_TRANSLATION_KEYS_MAP: { [key in HotelPolicySection]: string } = {
  CHILDREN: 'vacation.select.policies.children',
  PETS: 'vacation.select.policies.pets',
  GENERAL: 'vacation.select.policies.general',
  SMOKING: 'vacation.select.policies.smoking',
};

const POLICY_ICONS_MAP: { [key in HotelPolicySection]: StarfishIconRef } = {
  CHILDREN: { category: 'Services', name: 'icon-child', color: 'iconPrimary' },
  PETS: { category: 'Services', name: 'icon-pawn', color: 'iconPrimary' },
  GENERAL: { category: 'Semantics', name: 'icon-circle-info', color: 'iconPrimary' },
  SMOKING: { category: 'Services', name: 'icon-cigarette', color: 'iconPrimary' },
};

export const HOTEL_POLICY_SECTION_ORDER: HotelPolicySection[] = ['GENERAL', 'CHILDREN', 'PETS', 'SMOKING'];
